import { z } from 'zod';
import { brandingSchema } from './domains.js';
import { navigationDecisionSchema, normalizeOrigin } from './navigation.js';
import { productKeySchema } from './products.js';
import { applicationRegistrySchema } from './applications.js';
import { staffRoleSchema } from './roles.js';
/**
 * platform-api v1 route table. The server registers exactly these paths and the client calls them,
 * so the two cannot drift. Every error body is a problem document (errors.ts).
 */
export const PLATFORM_API = {
    live: '/health/live',
    ready: '/health/ready',
    openapi: '/docs/json',
    applications: '/v1/applications',
    resolveDomain: '/v1/domains/resolve',
    loginOptions: '/v1/login/options',
    loginDiscover: '/v1/login/discover',
    meAccess: '/v1/me/access',
    navigationDecide: '/v1/navigation/decide',
    invitationPreview: (token) => `/v1/invitations/${encodeURIComponent(token)}`,
    invitationAccept: (token) => `/v1/invitations/${encodeURIComponent(token)}/accept`,
    devFixtures: '/v1/dev-identity/fixtures',
    devSession: '/v1/dev-identity/session',
    devToken: '/v1/dev-identity/token',
    devJwks: '/v1/dev-identity/jwks.json',
    devServiceToken: '/v1/dev-identity/service-token',
    internalAccessCheck: '/v1/internal/access/check',
    internalInvitations: '/v1/internal/invitations',
    /** 0.19.0: GET an organization's intelligence level and module switches for one product (service tokens). */
    internalOrganizationSettings: (orgId) => `/v1/internal/organizations/${encodeURIComponent(orgId)}/settings`,
    /** 0.25.0: GET the members of an organization for one product: user id, name, email, status (service tokens). */
    internalOrganizationMembers: (orgId) => `/v1/internal/organizations/${encodeURIComponent(orgId)}/members`,
    adminMe: '/v1/admin/me',
    adminOrganizations: '/v1/admin/organizations',
    adminOrganization: (id) => `/v1/admin/organizations/${encodeURIComponent(id)}`,
};
/**
 * Access-token audiences of platform-api. The staff routes (every PLATFORM_API entry under
 * ADMIN_API_PATH_PREFIX) accept only tokens for the admin audience, which is the Trace Admin application's
 * own audience in the registry; the internal routes (INTERNAL_API_PATH_PREFIX, internal.ts) only service
 * tokens for the internal audience; every other authenticated route accepts only the platform audience. A
 * token for one is refused on the others, so a hub or product token can never call a staff or internal
 * route, a Trace Admin token can never act as a customer session, and a service token can never act as a
 * person. These are the defaults; an environment may set its own (PLATFORM_API_AUDIENCE,
 * ADMIN_API_AUDIENCE and INTERNAL_API_AUDIENCE in platform-api).
 */
export const DEFAULT_PLATFORM_API_AUDIENCE = 'urn:trace:api:platform';
export const DEFAULT_ADMIN_API_AUDIENCE = 'urn:trace:api:admin';
export const ADMIN_API_PATH_PREFIX = '/v1/admin/';
/** True for a staff route path or route pattern: requests to it carry an admin-audience token. */
export function isAdminApiPath(path) {
    return path === ADMIN_API_PATH_PREFIX.slice(0, -1) || path.startsWith(ADMIN_API_PATH_PREFIX);
}
/** GET /v1/applications: the public application registry for this environment. */
export const applicationsResponseSchema = applicationRegistrySchema;
/**
 * The account hub page of an invitation. The hub registers exactly this route, and every invitation
 * link (seed output, delivery adapters, emails) is built with invitationUrl, so they cannot drift apart.
 */
export const INVITATION_PATH = '/invitations/:token';
/** The hub path of one invitation; the token is encoded as a single path segment. */
export function invitationPath(token) {
    return INVITATION_PATH.replace(':token', encodeURIComponent(token));
}
/** The absolute invitation link on the account hub, for example https://app.trace.io/invitations/inv_x. */
export function invitationUrl(hubOrigin, token) {
    return `${normalizeOrigin(hubOrigin)}${invitationPath(token)}`;
}
/**
 * GET /v1/invitations/:token: public preview. The invited email is masked. A staff invitation (Trace Admin) belongs
 * to no customer organization: its organization is Trace itself, productKey is admin and staffRole names the role.
 */
export const invitationPreviewSchema = z.object({
    status: z.enum(['pending', 'accepted', 'expired', 'revoked']),
    kind: z.enum(['organization', 'portal', 'staff']),
    organization: z.object({ displayName: z.string(), branding: brandingSchema }),
    productKey: productKeySchema.optional(),
    staffRole: staffRoleSchema.optional(),
    portal: z.object({ displayName: z.string(), customerAccountName: z.string() }).optional(),
    emailHint: z.string(),
    expiresAt: z.iso.datetime(),
});
/** POST /v1/invitations/:token/accept (authenticated). */
export const invitationAcceptResponseSchema = z.object({
    contextId: z.string(),
    decision: navigationDecisionSchema,
});
/** GET /v1/admin/me: Trace staff identity. 403 for anyone who is not active staff. */
export const adminMeSchema = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    staffRole: staffRoleSchema,
    mfaSatisfied: z.boolean(),
    permissions: z.array(z.string()),
});
export const adminOrganizationSummarySchema = z.object({
    id: z.uuid(),
    slug: z.string(),
    displayName: z.string(),
    status: z.enum(['active', 'suspended']),
    memberCount: z.number().int().nonnegative(),
    products: z.array(z.object({ productKey: productKeySchema, status: z.string() })),
    createdAt: z.iso.datetime(),
});
/** GET /v1/admin/organizations?query=&cursor=&limit= (keyset pagination, limit <= 100). */
export const adminOrganizationListSchema = z.object({
    items: z.array(adminOrganizationSummarySchema),
    nextCursor: z.string().nullable(),
});
export const adminOrganizationDetailSchema = adminOrganizationSummarySchema.extend({
    branding: brandingSchema,
    domains: z.array(z.object({
        id: z.uuid(),
        host: z.string(),
        kind: z.enum(['employee-app', 'customer-portal']),
        applicationKey: productKeySchema,
        verificationStatus: z.string(),
        tlsStatus: z.string(),
        lifecycle: z.string(),
    })),
    ssoPolicy: z.object({ enforced: z.boolean(), provider: z.string(), connection: z.string() }).nullable(),
    /**
     * The Auth0 organization the product applications sign in to (org_...), or null when it is not linked yet: then
     * every product sign-in of its members is refused by Auth0 (Trace Admin, Link to Auth0). Optional so a client
     * reading an older platform-api still parses its answer.
     */
    auth0OrganizationRef: z.string().nullable().optional(),
    /**
     * 0.18.0 (additive): the organization's ticket code (NWF of NWF-142), or null when its tickets use Trace's global
     * T- numbers. Optional so a client reading an older platform-api still parses its answer.
     */
    ticketCode: z.string().nullable().optional(),
    /**
     * Internal organizations (additive): true when the organization is billing exempt (every module of its products,
     * no contract, usage never billed), with the reason staff gave. Optional so a client reading an older platform-api
     * still parses its answer.
     */
    billingExempt: z.boolean().optional(),
    billingExemptReason: z.string().nullable().optional(),
});
//# sourceMappingURL=api.js.map